import {
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import {
  BULK_DELETE_BUSINESS_CONFIRM_PROCESS_RESPOND,
  BULK_DELETE_BUSINESS_DO_SHOW_LOG,
  BULK_DELETE_BUSINESS_LOG_CHANGE_PAGE,
  BULK_DELETE_BUSINESS_LOG_SET_SHOWN,
  DO_BULK_DELETE_BUSINESS,
} from 'state/actionTypes';
import * as api from 'api/bulkDeleteBusiness';
import {
  clearLogErrors,
  initializeProcessState,
  prependBusinessError,
  resetLogData,
  setCurrentBusinessId,
  setGlobalError,
  setIsProcessing,
  setLogData,
  setLogErrors,
  setLogIsLoading,
  setLogModalShown,
  setLogPage,
  setProcessedCount,
  setProcessStatus,
  showConfirmProcessModal,
} from 'state/bulkDeleteBusiness/actions';
import { cancelRequestedSelector } from 'state/bulkDeleteBusiness/selectors';
import CustomError from 'utils/CustomError';
import ProcessStatus from '../../constants/bulkAction/processStatus';

export function* doBulkDeleteBusiness({ payload }) {
  const { businessIds } = payload;

  yield put(showConfirmProcessModal());
  const {
    payload: { proceed },
  } = yield take(BULK_DELETE_BUSINESS_CONFIRM_PROCESS_RESPOND);
  if (!proceed) {
    return;
  }

  yield put(setProcessStatus(ProcessStatus.InProgress));
  yield put(initializeProcessState(businessIds));
  yield put(setIsProcessing(true));

  let processed = 0;
  let errorCount = 0;
  let isGlobalError = false;
  let wasAborted = false;

  for (let i = 0; i < businessIds.length; i++) {
    const businessId = businessIds[i];

    yield put(setCurrentBusinessId(businessId));

    try {
      yield call(api.deleteBusiness, businessId);
    } catch (e) {
      if (e instanceof CustomError) {
        yield put(prependBusinessError(businessId, e.message));
        errorCount += 1;
      } else if (e instanceof Error) {
        isGlobalError = true;
        yield put(setGlobalError(e.message));
        break;
      }
    }
    processed += 1;
    yield put(setProcessedCount(processed));

    const cancelRequested = yield select(cancelRequestedSelector);
    if (cancelRequested) {
      wasAborted = true;
      break;
    }
  }

  if (isGlobalError) {
    yield put(setProcessStatus(ProcessStatus.Error));
  } else if (errorCount === processed) {
    yield put(setProcessStatus(ProcessStatus.AllFailed));
  } else if (processed === businessIds.length && errorCount > 0) {
    yield put(setProcessStatus(ProcessStatus.CompletedWithWarning));
  } else if (processed < businessIds.length && wasAborted) {
    yield put(setProcessStatus(ProcessStatus.Aborted));
  } else {
    yield put(setProcessStatus(ProcessStatus.Completed));
  }
}

function* logModalTask() {
  while (true) {
    const { page } = (yield take(BULK_DELETE_BUSINESS_LOG_CHANGE_PAGE)).payload;
    yield put(setLogIsLoading(true));
    yield put(clearLogErrors());
    yield put(resetLogData());
    try {
      const {
        data: {
          data: { internal_tools_activities: activities },
          paging,
        },
      } = yield call(api.getBulkDeleteBusinessLogs, page, 20);
      yield put(setLogData(activities, paging));
    } catch (e) {
      yield put(setLogErrors(e.message));
    } finally {
      yield put(setLogIsLoading(false));
    }
  }
}

export function* doShowLogs() {
  const task = yield fork(logModalTask);
  yield delay(30);
  yield put(setLogPage(1));
  yield put(setLogModalShown(true));
  let closeModalRequest;
  do {
    closeModalRequest = (yield take(BULK_DELETE_BUSINESS_LOG_SET_SHOWN))
      .payload;
  } while (closeModalRequest.shown);
  yield cancel(task);
}

export default function*() {
  yield takeLatest(DO_BULK_DELETE_BUSINESS, doBulkDeleteBusiness);
  yield takeLatest(BULK_DELETE_BUSINESS_DO_SHOW_LOG, doShowLogs);
}
